<script lang="ts">
  export * from './DatePicker.types';
</script>

<script setup lang="ts">
  // TODO: fix the wide array of types we have representing a date in this and related files

  /**
   * As of v3.0.0-alpha.7, all installation methods require manual import of component styles.
   * This is due to Vite build restrictions in libary mode.
   * @see: https://vcalendar.io/getting-started/installation.html#use-plugin
   */
  import 'v-calendar/dist/style.css';
  import { endOfDay, isAfter, isBefore, isMatch as isFormatMatch, isValid, parse, startOfDay } from 'date-fns';
  import merge from 'lodash-es/merge';
  import unionBy from 'lodash-es/unionBy';
  import uniqueId from 'lodash-es/uniqueId';
  import { DatePicker as VCalendarDatePicker } from 'v-calendar';
  import type { CalendarDay } from 'v-calendar/dist/types/src/utils/page.js';
  import { computed, ref, useSlots, useTemplateRef, watch } from 'vue';

  import { DATE_FORMATS } from '../../constants';
  import { t } from '../../locale';
  import createValidDate from '../../utils/createValidDate';
  import formatDateTime from '../../utils/formatDateTime';
  import normalizeDate from '../../utils/normalizeDate';
  import { getCalendarFocusDate } from '../../utils/templateRefNarrowing';
  import Button from '../Button/Button.vue';
  import Icon from '../Icon/Icon.vue';
  import Input from '../Input/Input.vue';
  import { DatePickerPopoverOptions, DatePickerProps } from './DatePicker.types';

  interface OnInputUpdateParams {
    /**
     * Value from the input field of the datepicker
     */
    dateString: string | number | null;

    /**
     * Manage update:model-value emission, as having @change in the input slot triggers double model updates.
     */
    shouldEmit?: boolean;

    /**
     * The v-calendar callback to synchronize the input field and internal value
     */
    inputChangeCb?: (e: { currentTarget: { value: null | string } }) => void;
  }

  const props = withDefaults(defineProps<DatePickerProps>(), {
    availableDates: undefined,
    addBottomSpace: false,
    disabled: false,
    disabledDates: undefined,
    errorText: '',
    format: DATE_FORMATS.YYYY_MM_DD_DATE_FNS,
    hideClearButton: false,
    hintText: '',
    id: '',
    isInputReadonly: false,
    label: '',
    maxDate: undefined,
    maxPage: undefined,
    minDate: undefined,
    minPage: undefined,
    mode: 'date',
    modelValue: '',
    name: '',
    placeholder: undefined,
    enableTeleport: false,
    timeZone: 'UTC',
  });

  const slots = useSlots();

  const emit = defineEmits<{
    (e: 'update:model-value', date: string | number | Date): void;
    (e: 'blur'): void;
  }>();

  /**
   * @see https://vcalendar.io/attributes.html
   */
  const attributes = ref([
    {
      key: 'today',
      content: 'blue',
      dates: new Date(),
    },
  ]);

  /**
   * @see https://vcalendar.io/datepicker.html#date
   */
  const dateValue = ref<Date | null | undefined>(createValidDate(props.modelValue));
  const masks = {
    input: props.format?.toUpperCase(),
    weekdays: 'W',
    inputDateTime: ['YYYY-MM-DD h:mm A'],
  };

  const isTimeInput = computed(() => props.mode === 'time');

  const dateFormat = computed(() => {
    if (isTimeInput.value) {
      return DATE_FORMATS.TIME_DATE_FNS;
    }
    if (props.mode === 'dateTime') {
      return DATE_FORMATS.YYYY_MM_DD_TIME_DATE_FNS;
    }
    return props.format || DATE_FORMATS.YYYY_MM_DD_DATE_FNS;
  });

  /**
   * For aria-describedby
   */
  const formatDescriptionId = uniqueId('format-description-');

  const formattedDate = computed(() => {
    if (!dateValue.value || (!isValid(normalizeDate(dateValue.value)) && !isTimeInput.value)) {
      return '';
    }

    return formatDateTime(dateValue.value, { format: dateFormat.value, timeZone: props.timeZone });
  });

  const mergedPopoverOptions = computed(() => {
    if (props.enableTeleport) {
      /* This object is used to configure the popper.js inside of the v-calendar.
       * Documentation can be found here https://popper.js.org/docs/v2/modifiers/
       */
      const teleportModifiers = [
        // Keep event listeners enabled so popper repositions on scroll/resize
        {
          name: 'eventListeners',
          options: { scroll: true, resize: true },
        },
      ];

      const userModifiers = props.popoverOptions?.modifiers;
      /**
       * Union the teleport modifiers with the user modifiers by the name of the modifier to avoid duplicating the modifiers.
       * Teleport modifiers take precedence over user modifiers when there are conflicts.
       * @example
       * // User modifiers
       * [
       *   { name: 'offset', options: { offset: [0, 10] } },
       *   { name: 'eventListeners', options: { scroll: false } }
       * ]
       *
       * // Teleport modifiers
       * [
       *   { name: 'eventListeners', options: { scroll: true } }
       * ]
       *
       * // Result: teleport's eventListeners wins, user's offset is kept
       * [
       *   { name: 'eventListeners', options: { scroll: true } },
       *   { name: 'offset', options: { offset: [0, 10] } }
       * ]
       * @see https://lodash.com/docs/4.17.15#unionBy
       */
      const mergedModifiers = !userModifiers?.length
        ? teleportModifiers
        : unionBy(teleportModifiers, userModifiers, 'name');

      const teleportPopoverOptions: DatePickerPopoverOptions = {
        positionFixed: true,
        modifiers: mergedModifiers,
      };

      // remove modifiers from the popover options to avoid duplicating the modifiers in the merge function below
      const { modifiers: _, ...userOptionsWithoutModifiers } = props.popoverOptions || {};

      return merge({ visibility: 'focus' }, userOptionsWithoutModifiers, teleportPopoverOptions);
    }
    return merge({ visibility: 'focus' }, props.popoverOptions);
  });

  const calendarRef = useTemplateRef('calendar');

  const placeholderText = computed(() => props.placeholder || props.format?.toUpperCase());

  function moveToToday() {
    const today = new Date();
    getCalendarFocusDate(calendarRef.value)?.(today);
  }

  function onDatePickerUpdate(value: Date | null, shouldEmit = false) {
    if (props.maxDate && value && isAfter(value, endOfDay(normalizeDate(props.maxDate)))) {
      return;
    }

    if (props.minDate && value && isBefore(value, startOfDay(normalizeDate(props.minDate)))) {
      return;
    }

    dateValue.value = value;

    if (shouldEmit) {
      emit('update:model-value', formattedDate.value);
    }
  }

  /**
   * Change event handler for the input field
   */
  function onInputUpdate({ dateString, shouldEmit = false, inputChangeCb }: OnInputUpdateParams) {
    const format = dateFormat.value;

    if (typeof dateString === 'number') {
      throw new Error('Date provided was type "number". Use "string" or "null"');
    }

    if (!dateString) {
      onDatePickerUpdate(null);
      inputChangeCb?.({ currentTarget: { value: null } });
      return;
    }

    if (!isFormatMatch(dateString, format)) {
      dateValue.value = null;
      inputChangeCb?.({ currentTarget: { value: null } });
      emit('update:model-value', formattedDate.value);
      return;
    }

    if (props.mode === 'date') {
      const normalizedDate = normalizeDate(dateString);

      if (!isValid(normalizedDate)) {
        return;
      }

      onDatePickerUpdate(normalizedDate, shouldEmit);
    } else {
      onDatePickerUpdate(parse(dateString, format, new Date()), shouldEmit);
    }
  }

  watch(
    () => props.modelValue,
    () => {
      onInputUpdate({ dateString: props.modelValue });
    },
  );

  function onDayClick(_: CalendarDay, event: MouseEvent) {
    // Hack to avoid a Vue 3.5+ reactivity issue when clicking a day in the VCalendar component
    // Important: this reduces 90% of the related errors on Sentry, but not all of them. Some situations like selecting a day over and over, may still throw the error.
    // See https://github.com/nathanreyes/v-calendar/issues/1498#issuecomment-2376294504
    (event.target as HTMLElement)?.blur();
  }
</script>

<template>
  <div class="relative" :class="$style['vc-overrides']">
    <VCalendarDatePicker
      ref="calendar"
      trim-weeks
      :attributes="attributes"
      :available-dates="availableDates"
      :disabled-dates="disabledDates"
      :masks="masks"
      :max-date="maxDate"
      :min-date="minDate"
      :max-page="maxPage"
      :min-page="minPage"
      :mode="mode"
      :model-value="dateValue"
      :popover="mergedPopoverOptions"
      :hide-time-header="isTimeInput"
      @dayclick="onDayClick"
      @update:model-value="(value) => onDatePickerUpdate(value, true)"
    >
      <template #header-left-button>
        <Icon class="icon rotate-180" name="chevron-right" />
      </template>
      <template #header-right-button>
        <Icon class="icon" name="chevron-right" />
      </template>

      <template #default="{ togglePopover, inputValue, inputEvents: { keyup, focusin, focusout, change } }">
        <span :id="formatDescriptionId" class="sr-only">
          {{ t('ll.datePicker.dateFormat') }}: {{ format?.toUpperCase() }}
        </span>
        <Input
          :id="props.id"
          :aria-describedby="formatDescriptionId"
          :add-bottom-space="props.addBottomSpace"
          :class="$style.input"
          :disabled="disabled"
          :error-text="props.errorText"
          :hint-text="props.hintText"
          :label="label"
          :model-value="inputValue"
          :name="name"
          :placeholder="placeholderText"
          :is-read-only="isInputReadonly"
          v-on="{ keyup, focusin, focusout }"
          @blur="emit('blur')"
          @change="(dateString) => onInputUpdate({ dateString, inputChangeCb: change })"
        >
          <template #append>
            <Button
              v-if="!isTimeInput && dateValue && !hideClearButton && !disabled"
              icon
              :class="$style['close-button']"
              @click.stop="dateValue = undefined"
            >
              <Icon name="close" />
            </Button>
            <Icon
              v-else
              :class="[$style['calendar-icon'], { 'cursor-pointer': !disabled }]"
              :name="isTimeInput ? 'recent' : 'calendar'"
              @click="!disabled && togglePopover()"
            />
          </template>
          <template v-if="slots.hint" #hint>
            <slot name="hint"></slot>
          </template>
        </Input>
      </template>

      <template #footer>
        <div class="pt-1.5 text-right">
          <Button inline class="mx-3 my-1.5" :class="[$style['button-today']]" @click="moveToToday">
            {{ t('ll.datePicker.today') }}
          </Button>
        </div>
      </template>
    </VCalendarDatePicker>
  </div>
</template>

<style module>
  @reference "../../../styles/main.css";

  /* These styles are intentionally excluded from the 'utilities' layer in order to override v-calendar's styles, which do not use CSS layers */
  .vc-overrides {
    :global(.vc-pane-container) {
      padding: --spacing(3);
    }

    :global(.vc-pane-header-wrapper) {
      width: calc(100% - --spacing(3) * 2);
    }

    :global(.vc-popover-caret) {
      display: none !important;
    }

    :global(.vc-popover-content) {
      border-radius: var(--radius-sm);
      border-color: var(--color-blue-500);
      margin-top: --spacing(1.5);
    }

    :global(.vc-header) {
      opacity: 1;
      background-color: var(--color-white);
      border-top-left-radius: var(--radius-sm);
      border-top-right-radius: var(--radius-sm);
      height: 50px;

      :global(.vc-title-wrapper) {
        z-index: 1;

        :global(.vc-title) {
          background-color: var(--color-white);
          color: var(--color-ice-700);
          font-family: Sofia, sans-serif;
          font-size: var(--text-base);
          font-weight: var(--font-weight-medium);
          line-height: var(--line-height-base);
          padding-bottom: 0.5rem; /* 8px */
          padding-top: 0;
        }
      }
    }

    :global(.vc-arrows-container) {
      padding: --spacing(3) 0 0 0;
      width: calc(100% - (--spacing(3) * 2));

      :global(.vc-arrow) {
        color: var(--color-ice-700);
        height: 48px;
        margin: 0.15rem;
        width: 48px;
      }
    }

    :global(.vc-weeks) {
      font-family: Sofia, sans-serif;
      gap: --spacing(1.5) 0;
      padding: 0;
    }

    :global(.vc-weekday) {
      background-color: var(--color-white);
      color: var(--color-ice-700);
      font-size: var(--text-sm);
      font-weight: 200;
      height: 48px;
      margin-bottom: calc(--spacing(1.5) * -1);
      padding: 18px 0; /* to vertically center the text */
      text-transform: capitalize;
      width: 48px;
    }

    :global(.vc-day) {
      color: var(--color-ice-900);
      height: 48px;
      width: 48px;

      &:global(.is-not-in-month) * {
        opacity: 1;
        color: var(--color-ice-500);
      }
    }

    :global(.vc-day-content) {
      border-radius: var(--radius-sm);
      font-size: var(--text-sm);
      font-weight: var(--font-weight-medium) !important;
      height: 100%;
      width: 100%;

      &:focus {
        outline: #0072f026 solid 3px;
      }
    }

    :global(.vc-content) {
      color: var(--color-blue-500) !important;
    }

    :global(.vc-content.vc-highlight-content-solid) {
      color: var(--color-white) !important;
    }

    :global(.vc-highlight) {
      background-color: var(--color-blue-500) !important;
      border-radius: var(--radius-sm) !important;
      height: 100%;
      width: 100%;
    }

    :global(.vc-time-select-group select) {
      border: 0;
    }
  }

  .close-button {
    padding: 0;
    height: --spacing(6);
    width: --spacing(6);
  }

  .button-today {
    border: none;
    box-shadow: none;
    color: var(--color-blue-500);
    font-family: Sofia, sans-serif;
    font-size: var(--text-sm);
    font-weight: var(--font-weight-normal);
    line-height: var(--line-height-body);
    text-decoration: none;

    &:focus {
      outline: #0072f026 solid 3px;
    }

    &:focus,
    &:hover {
      text-decoration: none;
    }
  }

  /* Override default read-only styling */
  .input input:read-only:not(:disabled) {
    border-color: var(--color-ice-500);
    background-color: var(--color-white) !important;
    cursor: default;
    pointer-events: initial;
    padding: 0 --spacing(3);
  }

  .calendar-icon {
    bottom: 0.3125rem; /* 5px */
    right: 0.3125rem; /* 5px */
  }
</style>
